import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import CustomLabel from "../components/CustomLabel";
import CustomButton from "../components/CustomButton";
import { COLORS } from "../assets/theme";
import GenericFormDialog from "../components/GenericFormDialog";
import { getAllVenues, createVenue } from "../services/VenueService";

const user = {
  name: "ayman sbeity",
};

const Home: React.FC = () => {
  const [venues, setVenues] = useState<any[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fields = [
    {
      type: "text" as const,
      name: "name",
      label: "Venue Name",
      required: true,
    },
    {
      type: "number" as const,
      name: "orderNumber",
      label: "Order Number",
    },
    {
      type: "text" as const,
      name: "venueOverlay",
      label: "Venue Overlay",
    },
    { type: "file" as const, name: "image", label: "Image URL" },
  ];

  const handleAddVenue = () => {
    setDialogOpen(true);
  };

  const handleDialogSubmit = async (values: Record<string, any>) => {
    try {
      setLoading(true);
      const parsedOrderNumber =
        values.orderNumber !== undefined && values.orderNumber !== ""
          ? parseInt(String(values.orderNumber), 10)
          : undefined;
      await createVenue({
        name: values.name,
        image: values.image,
        venueOverlay: values.venueOverlay ?? "",
        ...(parsedOrderNumber !== undefined && !Number.isNaN(parsedOrderNumber)
          ? { orderNumber: parsedOrderNumber }
          : {}),
      });
      const updated = await getAllVenues();
      setVenues(Array.isArray(updated) ? mapApiVenues(updated) : []);
      setDialogOpen(false);
    } catch (error) {
      // fallback to local state
      const imageValue = values.image;
      const imagePreview =
        imageValue && typeof imageValue === "object" && "name" in imageValue
          ? URL.createObjectURL(imageValue as File)
          : values.image || "/favicon.ico";
      setVenues([
        ...venues,
        {
          name: values.name || "Unnamed Venue",
          image: imagePreview,
          venueOverlay: values.venueOverlay ?? "",
          orderNumber:
            values.orderNumber !== undefined &&
            values.orderNumber !== "" &&
            !Number.isNaN(parseInt(String(values.orderNumber), 10))
              ? parseInt(String(values.orderNumber), 10)
              : undefined,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const data = await getAllVenues();
        setVenues(Array.isArray(data) ? mapApiVenues(data) : []);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, []);

  const navigate = useNavigate();

  const handleVenueClick = (venue: {
    id?: string;
    name: string;
    image: string;
    user_id?: string;
  }) => {
    const venueId = venue.id || venue.user_id || "unknown";
    const branchId = encodeURIComponent(venueId);
    navigate(`/dashboard/${branchId}`, { state: { venue, venueId } });
  };

  function mapApiVenues(
    items: any[]
  ): { id?: string; name: string; image: string; user_id?: string }[] {
    return items.map((v) => {
      const id = v.id || v._id || undefined;
      const name = (v.name && String(v.name).trim()) || "Unnamed Venue";
      const rawImage = v.banner ?? v.image ?? v.image_url ?? "";
      const image =
        rawImage && String(rawImage).trim()
          ? String(rawImage).trim()
          : "/favicon.ico";
      const user_id = v.user_id ? String(v.user_id).trim() : undefined;
      return { id, name, image, user_id };
    });
  }

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: COLORS.WhiteBackground,
      }}
    >
      <Box
        sx={{
          flex: 1,
          p: 0,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flex: 1, px: 4, py: 5 }}>
          <CustomLabel
            text="My Venues"
            fontWeight={700}
            fontSize="1.5rem"
            color={COLORS.MainColor}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <CustomLabel
              text={"Choose a venue to continue or"}
              sx={{ display: "inline" }}
            />
            <CustomButton
              variant="outlined"
              label="Add New"
              textColor={COLORS.MainColor}
              borderColor={COLORS.MainColor}
              borderRadius={2}
              sx={{ px: 2, ml: 2, fontWeight: 700 }}
              onClick={handleAddVenue}
            />
            <GenericFormDialog
              open={dialogOpen}
              title="Add New Venue"
              onClose={() => setDialogOpen(false)}
              onSubmit={handleDialogSubmit}
              fields={fields}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            {venues.length === 0 ? (
              <CustomLabel text="No venues yet." sx={{ color: "#888" }} />
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gap: 5,
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(4, 1fr)",
                  },
                }}
              >
                {venues.map((venue, idx) => {
                  const venueName =
                    venue.name && venue.name.trim()
                      ? venue.name
                      : "Unnamed Venue";
                  const venueImage =
                    venue.image && venue.image.trim()
                      ? venue.image
                      : "/favicon.ico";
                  return (
                    <Box key={venueName + idx}>
                      <Box
                        component={"div"}
                        sx={{
                          bgcolor: COLORS.textFieldBackgroundColor,
                          borderRadius: 6,
                          boxShadow: 4,
                          height: 300,
                          display: "flex",
                          flexDirection: "column",
                          transition: "box-shadow 0.2s, background 0.2s",
                          cursor: "pointer",
                          "&:hover": {
                            boxShadow: 8,
                            bgcolor: COLORS.WhiteBackground,
                          },
                        }}
                        onClick={() => handleVenueClick(venue)}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            height: "80%",
                            position: "relative",
                            minHeight: 0,
                            overflow: "hidden",
                            borderTopLeftRadius: "inherit",
                            borderTopRightRadius: "inherit",
                          }}
                        >
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              backgroundImage: `url(${venueImage})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                              backgroundRepeat: "no-repeat",
                              borderTopLeftRadius: "inherit",
                              borderTopRightRadius: "inherit",
                            }}
                          />
                        </Box>
                        <Box
                          sx={{
                            width: "100%",
                            height: "15%",
                            display: "flex",
                            alignItems: "flex-end",
                            justifyContent: "center",
                            bgcolor: "transparent",
                            borderBottomLeftRadius: "inherit",
                            borderBottomRightRadius: "inherit",
                            pb: 1,
                          }}
                        >
                          <CustomLabel
                            text={venueName}
                            fontWeight={700}
                            fontSize="1rem"
                            color={COLORS.MainColor}
                            sx={{
                              textAlign: "center",
                              whiteSpace: "nowrap",
                              letterSpacing: 1,
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Home;
